import { useMutation } from '@tanstack/react-query';
import { useCallback, useState } from 'preact/hooks';
import { ApiEndpoints } from 'types/api';

type Vote = -1 | 1 | 0;
type Loading = 'VOTE_UP' | 'VOTE_DOWN' | null;

interface Request {
  review_id: number;
  vote: Vote;
  client_id: string;
}

interface Response {
  dislike: number;
  like: number;
  vote: number;
}

async function makeVote(request: Request) {
  const response = await fetch(ApiEndpoints.VOTE, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new Error(await response.text());
  }

  const json: Response = await response.json();

  return json;
}

export function useVoting(reviewId: number, clientId: string) {
  const [isLoading, setIsLoading] = useState<Loading>(null);
  const { mutateAsync } = useMutation<Response, unknown, Request, unknown>({
    mutationFn: makeVote,
    onMutate: ({ vote }) => {
      setIsLoading(vote === 1 ? 'VOTE_UP' : vote === -1 ? 'VOTE_DOWN' : null);
    },
    onError: () => {
      console.error('Webtex Reviews: Error applying your vote');
    },
    onSettled: () => {
      setIsLoading(null);
    },
  });

  const vote = useCallback(
    async (value: Vote) => {
      try {
        const { like, dislike } = await mutateAsync({
          client_id: clientId,
          review_id: reviewId,
          vote: value,
        });

        return [like, dislike];
      } catch (e) {
        return [0, 0];
      }
    },
    [mutateAsync, clientId, reviewId],
  );

  const voteUp = () => vote(1);
  const voteDown = () => vote(-1);

  return {
    voteUp,
    voteDown,
    isVoting: isLoading,
  };
}
